import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiClient } from '../../../utils/apiClient';

interface PricingItem {
  id: string;
  name: string;
  current_price: string | null;
  is_set: boolean;
}

interface CustomerPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
}

const CustomerPricingModal: React.FC<CustomerPricingModalProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName
}) => {
  const { currentBusiness, token, API_BASE_URL, csrfToken, isCookieMode } = useAuth();
  const api = useApiClient();
  const [items, setItems] = useState<PricingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editedPrices, setEditedPrices] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && customerId) {
      fetchPricing();
    }
  }, [isOpen, customerId]);

  const fetchPricing = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ items: PricingItem[] }>(`/customers/${customerId}/pricing`);
      setItems(response.items);
      // Initialize edited prices with current values
      const initialPrices: Record<string, string> = {};
      response.items.forEach((item: PricingItem) => {
        if (item.is_set && item.current_price) {
          initialPrices[item.id] = item.current_price;
        }
      });
      setEditedPrices(initialPrices);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (itemId: string, value: string) => {
    if (value === '') {
      // Remove the price
      const newPrices = { ...editedPrices };
      delete newPrices[itemId];
      setEditedPrices(newPrices);
    } else {
      // Update the price
      setEditedPrices(prev => ({
        ...prev,
        [itemId]: value
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      // Prepare pricing updates
      const pricingUpdates = items.map(item => ({
        item_id: item.id,
        rate: editedPrices[item.id] || null
      }));

      await api.put(`/customers/${customerId}/pricing`, { pricing: pricingUpdates });

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setEditedPrices({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
              Edit Customer Prices
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              {customerName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="text-center py-8">
              <div className="text-neutral-600 dark:text-neutral-400">Loading pricing data...</div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-4">
              <div className="text-red-800 dark:text-red-200">{error}</div>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Set custom prices for this customer. Leave empty to use default pricing.
                </p>
              </div>

              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900 dark:text-neutral-50">
                        {item.name}
                      </div>
                      <div className="text-sm text-neutral-600 dark:text-neutral-400">
                        {item.is_set ? 'Custom price set' : 'Not Set'}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="relative">
                        <FiDollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={16} />
                                                 <input
                           type="text"
                           value={editedPrices[item.id] || ''}
                           onChange={(e) => handlePriceChange(item.id, e.target.value)}
                           placeholder={item.is_set && item.current_price ? item.current_price : '0.00'}
                           className="pl-8 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand dark:bg-neutral-800 dark:text-neutral-50 w-32"
                         />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {items.length === 0 && (
                <div className="text-center py-8 text-neutral-600 dark:text-neutral-400">
                  No customer-specific items found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave className="mr-2" size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerPricingModal; 